'use client'

import { useState, useEffect } from 'react'
import { Link } from '@/i18n/navigation'
import { X } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { getVerifiedClaims, claimsToUserShape } from '@/lib/supabase/claims'
import { Button } from '@/components/ui/button'

const VIEWS_KEY = 'theduchy_content_views'
const DISMISSED_KEY = 'theduchy_upgrade_dismissed'

/**
 * Engagement prompt that tracks content page views in sessionStorage.
 * Shows upgrade CTAs at milestone view counts (3 and 10+).
 * Only shown to non-authenticated users. Dismissable per session.
 */
export function UpgradePrompt() {
  const [viewCount, setViewCount] = useState(0)
  const [dismissed, setDismissed] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(true) // default true to avoid flash

  useEffect(() => {
    // Check auth state
    const supabase = createClient()
    getVerifiedClaims(supabase)
      .then((claims) => {
        const user = claims ? claimsToUserShape(claims) : null
        setIsAuthenticated(!!user)
        if (user) return // Don't track or show for authenticated users

        // Blocked site-data / private mode / sandboxed context: even READING
        // window.sessionStorage throws a SecurityError ("Access is denied for
        // this document"), so the whole access is wrapped — not just the parse.
        // Without this (and the .catch below) it surfaced as an unhandled
        // rejection: THEDUCHY-WEBSITE-46. We just skip the view-count nudge.
        try {
          const stored = parseInt(sessionStorage.getItem(VIEWS_KEY) || '0', 10)
          const newCount = stored + 1
          sessionStorage.setItem(VIEWS_KEY, String(newCount))
          setViewCount(newCount)
          setDismissed(sessionStorage.getItem(DISMISSED_KEY) === 'true')
        } catch {
          /* storage unavailable — no view-count nudge this session */
        }
      })
      .catch(() => {
        /* claims lookup failed — nothing to track or show */
      })
  }, [])

  const handleDismiss = () => {
    setDismissed(true)
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true')
    } catch {
      /* storage unavailable — dismissal just won't persist this session */
    }
  }

  // Don't show for authenticated users, dismissed state, or under threshold
  if (isAuthenticated || dismissed || viewCount < 3) {
    return null
  }

  // Prominent banner at 10+ views
  if (viewCount >= 10) {
    return (
      <div className="bg-brand-700 text-white">
        {/* lg:px-12 keeps the copy + CTA clear of the reader pages' fixed
            edge-docked panel re-open tabs (RailReopenHandle, ~28px). */}
        <div className="mx-auto max-w-7xl px-4 py-4 lg:px-12 flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold">
              You&apos;ve explored {viewCount} tutorials — unlock hundreds more with a membership!
            </p>
            <p className="text-xs text-brand-200 mt-0.5">
              Get full access to every tutorial, course, and community feature.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link href="/auth?mode=register">
              <Button size="sm" className="bg-white text-brand-700 hover:bg-brand-50 font-semibold">
                Join Now
              </Button>
            </Link>
            <button
              onClick={handleDismiss}
              className="p-1 text-brand-200 hover:text-white transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Subtle banner at 3-9 views
  return (
    <div className="bg-brand-50 border-b border-brand-100">
      <div className="mx-auto max-w-7xl px-4 py-3 lg:px-12 flex items-center justify-between gap-4">
        <p className="text-sm text-brand-800">
          You&apos;ve viewed {viewCount} tutorials — join to unlock hundreds more
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Link href="/auth?mode=register">
            <Button size="sm" variant="outline" className="border-brand-300 text-brand-700 hover:bg-brand-100 text-xs">
              Learn More
            </Button>
          </Link>
          <button
            onClick={handleDismiss}
            className="p-1 text-brand-400 hover:text-brand-700 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
